import React, { Fragment, FunctionComponent } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import { history } from 'helpers/history';
import { getCountryName } from 'helpers/countries';
import { isClienteRole } from 'helpers/roles';
import useTypedSelector from 'hooks/useTypedSelector';
import { userSelector } from 'redux/selectors/auth.selector';

import {
  ViewContainer,
  ViewContent,
  TitleContainer,
  Title,
  TableContainer,
  CustomIconButton,
  FilePreview,
} from 'styles/view';

import { IJewishnessShowProps } from './types';
import './i18n';

const JewishnessShow: FunctionComponent<IJewishnessShowProps> = (props: IJewishnessShowProps) => {
  const { item, showTitle = true } = props;
  const user = useTypedSelector(userSelector);

  return (
    <ViewContainer auth data-testid="jewishness-show">
      <ViewContent auth>
        {showTitle && (
          <TitleContainer>
            <Title style={{ flex: 1, textAlign: 'center' }}>
              {i18next.t<string>('JEWISHNESS_SHOW:TITLE')}
            </Title>
            {(!isClienteRole(user) || item?.state === 'OPENED') && (
              <CustomIconButton
                onClick={() => history.push(`/public/procedures/forms/jewishness/${item?.id}/edit`)}
              >
                <EditOutlined />
              </CustomIconButton>
            )}
          </TitleContainer>
        )}
        {item && (
          <>
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_SHOW:PERSON_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:LAST_NAME')}</td>
                  <td>{item.person.lastName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:FIRST_NAME')}</td>
                  <td>{item.person.firstName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_TYPE')}</td>
                  <td>{item.person.idCardTypeName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD')}</td>
                  <td>{item.person.idCard}</td>
                </tr>
                {item.person.idCardFile && (
                  <tr>
                    <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.idCardFile} title="idCardFile" />
                    </td>
                  </tr>
                )}
                {item.person.idCardBackFile && (
                  <tr>
                    <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_BACK_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.idCardBackFile} title="idCardBackFile" />
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:COUNTRY')}</td>
                  <td>{getCountryName(item.person.country)}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:CIVIL_STATE')}</td>
                  <td>{item.person.civilStateName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTHDATE')}</td>
                  <td>{item.person.birthdate}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:PHONE')}</td>
                  <td>{`${item.person.areaCode ? `(${item.person.areaCode}) ` : ''}${
                    item.person.phone
                  }`}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:EMAIL')}</td>
                  <td>{item.person.email}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:COMMUNITY')}</td>
                  <td>{item.person.community}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:TRIBE')}</td>
                  <td>{item.person.tribeName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('JEWISHNESS_SHOW:WEDDING_PLACE')}</td>
                  <td>{item.person.weddingPlace}</td>
                </tr>
                {item.person.birthCertificateFile && (
                  <tr>
                    <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTH_CERTIFICATE_FILE')}</td>
                    <td>
                      <FilePreview
                        src={item.person.birthCertificateFile}
                        title="birthCertificateFile"
                      />
                    </td>
                  </tr>
                )}
                {item.person.ketubaFile && (
                  <tr>
                    <td>{i18next.t<string>('JEWISHNESS_SHOW:KETUBA_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.ketubaFile} title="ketubaFile" />
                    </td>
                  </tr>
                )}
                {item.person.marriageCertificateFile && (
                  <tr>
                    <td>{i18next.t<string>('JEWISHNESS_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                    <td>
                      <FilePreview
                        src={item.person.marriageCertificateFile}
                        title="marriageCertificateFile"
                      />
                    </td>
                  </tr>
                )}
                {item.person.getFile && (
                  <tr>
                    <td>{i18next.t<string>('JEWISHNESS_SHOW:GET_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.getFile} title="getFile" />
                    </td>
                  </tr>
                )}
              </tbody>
            </TableContainer>
            {item.spouse && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('JEWISHNESS_SHOW:SPOUSE_INFORMATION')}
                </Typography>
                <br />
                <TableContainer>
                  <tbody>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:LAST_NAME')}</td>
                      <td>{item.spouse.lastName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:FIRST_NAME')}</td>
                      <td>{item.spouse.firstName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_TYPE')}</td>
                      <td>{item.spouse.idCardTypeName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD')}</td>
                      <td>{item.spouse.idCard}</td>
                    </tr>
                    {item.spouse.idCardFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_FILE')}</td>
                        <td>
                          <FilePreview src={item.spouse.idCardFile} title="idCardFile" />
                        </td>
                      </tr>
                    )}
                    {item.spouse.idCardBackFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_BACK_FILE')}</td>
                        <td>
                          <FilePreview src={item.spouse.idCardBackFile} title="idCardBackFile" />
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:COUNTRY')}</td>
                      <td>{getCountryName(item.spouse.country)}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTHDATE')}</td>
                      <td>{item.spouse.birthdate}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:TRIBE')}</td>
                      <td>{item.spouse.tribeName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:EMAIL')}</td>
                      <td>{item.spouse.email}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:COMMUNITY')}</td>
                      <td>{item.spouse.community}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:ALIVE')}</td>
                      <td>
                        {item.spouse.alive
                          ? i18next.t<string>('JEWISHNESS_SHOW:YES')
                          : i18next.t<string>('JEWISHNESS_SHOW:NO')}
                      </td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:CEMENTERY')}</td>
                      <td>
                        {item.spouse.alive
                          ? '-'
                          : item.spouse.cementeryName || item.spouse.cementery}
                      </td>
                    </tr>
                  </tbody>
                </TableContainer>
              </>
            )}
            {item.person.parents && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('JEWISHNESS_SHOW:PERSON_PARENTS_INFORMATION')}
                </Typography>
                <br />
                <TableContainer>
                  <tbody>
                    {['father', 'mother'].map((parentValue) => {
                      const parent = item.person.parents?.[parentValue as 'father' | 'mother'];
                      return (
                        !!parent && (
                          <Fragment key={`${parentValue}-parents-information`}>
                            <tr>
                              <td>
                                {i18next.t<string>(
                                  `JEWISHNESS_SHOW:${parentValue.toUpperCase()}_LAST_NAME`,
                                )}
                              </td>
                              <td>{parent.lastName}</td>
                            </tr>
                            <tr>
                              <td>
                                {i18next.t<string>(
                                  `JEWISHNESS_SHOW:${parentValue.toUpperCase()}_FIRST_NAME`,
                                )}
                              </td>
                              <td>{parent.firstName}</td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_TYPE')}</td>
                              <td>{parent.idCardTypeName}</td>
                            </tr>
                            <tr>
                              <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD')}</td>
                              <td>{parent.idCard}</td>
                            </tr>
                            {parent.idCardFile && (
                              <tr>
                                <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_FILE')}</td>
                                <td>
                                  <FilePreview src={parent.idCardFile} title="idCardFile" />
                                </td>
                              </tr>
                            )}
                            {parent.idCardBackFile && (
                              <tr>
                                <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_BACK_FILE')}</td>
                                <td>
                                  <FilePreview src={parent.idCardBackFile} title="idCardBackFile" />
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTHDATE')}</td>
                              <td>{parent.birthdate}</td>
                            </tr>
                          </Fragment>
                        )
                      );
                    })}
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:WEDDING_PLACE')}</td>
                      <td>{item.person.parents?.weddingPlace}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:WEDDING_DATE')}</td>
                      <td>{item.person.parents.weddingDate}</td>
                    </tr>
                    {item.person.parents.ketubaFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:KETUBA_FILE')}</td>
                        <td>
                          <FilePreview src={item.person.parents.ketubaFile} title="ketubaFile" />
                        </td>
                      </tr>
                    )}
                    {item.person.parents.marriageCertificateFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                        <td>
                          <FilePreview
                            src={item.person.parents.marriageCertificateFile}
                            title="marriageCertificateFile"
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </TableContainer>
              </>
            )}
            {item.person.grandParents && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('JEWISHNESS_SHOW:PERSON_GRANDPARENTS_INFORMATION')}
                </Typography>
                <br />
                <TableContainer>
                  <tbody>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:PATERNAL_FATHER_LAST_NAME')}</td>
                      <td>{item.person.grandParents.paternalFather.lastName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:PATERNAL_FATHER_FIRST_NAME')}</td>
                      <td>{item.person.grandParents.paternalFather.firstName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTHPLACE')}</td>
                      <td>{item.person.grandParents.paternalFather.birthplace}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:PATERNAL_MOTHER_LAST_NAME')}</td>
                      <td>{item.person.grandParents.paternalMother.lastName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:PATERNAL_MOTHER_FIRST_NAME')}</td>
                      <td>{item.person.grandParents.paternalMother.firstName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTHPLACE')}</td>
                      <td>{item.person.grandParents.paternalMother.birthplace}</td>
                    </tr>
                    {item.person.grandParents.paternalKetubaFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:KETUBA_FILE')}</td>
                        <td>
                          <FilePreview
                            src={item.person.grandParents.paternalKetubaFile}
                            title="ketubaFile"
                          />
                        </td>
                      </tr>
                    )}
                    {item.person.grandParents.paternalMarriageCertificateFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                        <td>
                          <FilePreview
                            src={item.person.grandParents.paternalMarriageCertificateFile}
                            title="marriageCertificateFile"
                          />
                        </td>
                      </tr>
                    )}
                    {item.person.grandParents.paternalCemeteryBurialCertificateFile && (
                      <tr>
                        <td>
                          {i18next.t<string>('JEWISHNESS_SHOW:CEMENTERY_BURIAL_CERTIFICATE_FILE')}
                        </td>
                        <td>
                          <FilePreview
                            src={item.person.grandParents.paternalCemeteryBurialCertificateFile}
                            title="cemeteryBurialCertificateFile"
                          />
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:MATERNAL_FATHER_LAST_NAME')}</td>
                      <td>{item.person.grandParents.maternalFather.lastName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:MATERNAL_FATHER_FIRST_NAME')}</td>
                      <td>{item.person.grandParents.maternalFather.firstName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTHPLACE')}</td>
                      <td>{item.person.grandParents.maternalFather.birthplace}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:MATERNAL_MOTHER_LAST_NAME')}</td>
                      <td>{item.person.grandParents.maternalMother.lastName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:MATERNAL_MOTHER_FIRST_NAME')}</td>
                      <td>{item.person.grandParents.maternalMother.firstName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('JEWISHNESS_SHOW:BIRTHPLACE')}</td>
                      <td>{item.person.grandParents.maternalMother.birthplace}</td>
                    </tr>
                    {item.person.grandParents.maternalKetubaFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:KETUBA_FILE')}</td>
                        <td>
                          <FilePreview
                            src={item.person.grandParents.maternalKetubaFile}
                            title="ketubaFile"
                          />
                        </td>
                      </tr>
                    )}
                    {item.person.grandParents.maternalMarriageCertificateFile && (
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                        <td>
                          <FilePreview
                            src={item.person.grandParents.maternalMarriageCertificateFile}
                            title="marriageCertificateFile"
                          />
                        </td>
                      </tr>
                    )}
                    {item.person.grandParents.maternalCemeteryBurialCertificateFile && (
                      <tr>
                        <td>
                          {i18next.t<string>('JEWISHNESS_SHOW:CEMENTERY_BURIAL_CERTIFICATE_FILE')}
                        </td>
                        <td>
                          <FilePreview
                            src={item.person.grandParents.maternalCemeteryBurialCertificateFile}
                            title="cemeteryBurialCertificateFile"
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </TableContainer>
              </>
            )}
            {item.person.witnesses && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('JEWISHNESS_SHOW:PERSON_WITNESSES_INFORMATION')}
                </Typography>
                <br />
                {item.person?.witnesses?.map((witness, i) => (
                  <TableContainer key={`${witness}-${i.toString()}-information`}>
                    <tbody>
                      <tr>
                        <td>{`${i18next.t<string>('JEWISHNESS_SHOW:WITNESS_NAME')} ${i + 1}`}</td>
                        <td>{witness.name}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_TYPE')}</td>
                        <td>{witness.idCardTypeName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD')}</td>
                        <td>{witness.idCard}</td>
                      </tr>
                      {witness.idCardFile && (
                        <tr>
                          <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_FILE')}</td>
                          <td>
                            <FilePreview src={witness.idCardFile} title="idCardFile" />
                          </td>
                        </tr>
                      )}
                      {witness.idCardBackFile && (
                        <tr>
                          <td>{i18next.t<string>('JEWISHNESS_SHOW:ID_CARD_BACK_FILE')}</td>
                          <td>
                            <FilePreview src={witness.idCardBackFile} title="idCardBackFile" />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:ADDRESS')}</td>
                        <td>{witness.address}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('JEWISHNESS_SHOW:PHONE')}</td>
                        <td>{`${witness.areaCode ? `(${witness.areaCode}) ` : ''}${
                          witness.phone
                        }`}</td>
                      </tr>
                      {witness.ketubaFile && (
                        <tr>
                          <td>{i18next.t<string>('JEWISHNESS_SHOW:KETUBA_FILE')}</td>
                          <td>
                            <FilePreview src={witness.ketubaFile} title="ketubaFile" />
                          </td>
                        </tr>
                      )}
                      {witness.marriageCertificateFile && (
                        <tr>
                          <td>{i18next.t<string>('JEWISHNESS_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                          <td>
                            <FilePreview
                              src={witness.marriageCertificateFile}
                              title="marriageCertificateFile"
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </TableContainer>
                ))}
              </>
            )}
            {item.comments && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('JEWISHNESS_SHOW:COMMENTS')}
                </Typography>
                <br />
                {item.comments}
              </>
            )}
          </>
        )}
      </ViewContent>
    </ViewContainer>
  );
};

export default JewishnessShow;
